import { gql } from '@apollo/client';
import client from 'apollo/client';
import { getPlanFilters } from 'common/filters/requestFilters';

/**
 * Create plan method
 * @param  {Object} payload
 * @return {Promise}
 */
const create = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createPlan($input: CreatePlanInput!) {
        createPlan(input: $input) {
          id
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * Find plan method
 * @param  {string} id
 * @return {Promise}
 */
const find = (id) =>
  client.query({
    query: gql`
      query plan($id: ID!) {
        plan(id: $id) {
          id
          currency
          description
          image_url
          name
          type
          platform
          price
          price_usd
          price_cop
          stripe_price_id
          payment_link
          sessions
          is_subscribable
          subscription_interval
          active_discount_percent
          org_benefit {
            discount_percent
            base_price
            discounted_price
            currency
            organization_name
          }
          session_types {
            id
            name
          }
        }
      }
    `,
    variables: { id },
    fetchPolicy: 'network-only'
  });

/**
 * List plans method
 * @param {Object} payload
 * @returns {Promise}
 */
const list = (payload) => {
  const { pagination, filters } = payload;
  const planFilters = getPlanFilters(filters);

  return client.query({
    query: gql`
      query plans {
        plans(
          first: ${pagination.first}
          page: ${pagination.page}
          ${planFilters.currency}
          ${planFilters.soft_delete}
          orderBy: {
            column: NAME,
            order: ASC
          }
        ) {
          paginatorInfo {
            currentPage
            hasMorePages
            total
          }
          data {
            id
            currency
            name
            description
            image_url
            type
            platform
            price
            price_usd
            price_cop
            stripe_price_id
            payment_link
            sessions
            is_subscribable
            subscription_interval
            active_discount_percent
            org_benefit {
              discount_percent
              base_price
              discounted_price
              currency
              organization_name
            }
            clients
            session_types {
              id
              name
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });
};

/**
 * List all plans method
 * @returns {Promise}
 */
const listAll = () =>
  client.query({
    query: gql`
      query allPlans {
        allPlans {
          id
          name
          description
          image_url
          type
          platform
          price_usd
          price_cop
          stripe_price_id
          payment_link
          sessions
          is_subscribable
          subscription_interval
          active_discount_percent
          org_benefit {
            discount_percent
            base_price
            discounted_price
            currency
            organization_name
          }
          session_types {
            id
            name
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });

/**
 * Get client allowed session types (categories) based on their active payments
 * @param  {number} userId
 * @return {Promise}
 */
const clientAllowedSessionTypes = (userId) =>
  client.query({
    query: gql`
      query clientAllowedSessionTypes($user_id: Int!) {
        clientAllowedSessionTypes(user_id: $user_id) {
          id
          name
        }
      }
    `,
    variables: { user_id: userId },
    fetchPolicy: 'network-only'
  });

/**
 * Remove plans method
 * @param  {Array} ids
 * @return {Promise}
 */
const remove = (ids) =>
  client.mutate({
    mutation: gql`
      mutation deletePlans($id: [ID!]!) {
        deletePlans(id: $id) {
          id
        }
      }
    `,
    variables: { id: ids }
  });

/**
 * Restore plans method
 * @param  {Array} ids
 * @return {Promise}
 */
const restore = (ids) =>
  client.mutate({
    mutation: gql`
      mutation restorePlans($id: [ID!]!) {
        restorePlans(id: $id) {
          id
        }
      }
    `,
    variables: { id: ids }
  });

/**
 * Update plan method
 * @param  {Object} payload
 * @return {Promise}
 */
const update = (payload) =>
  client.mutate({
    mutation: gql`
      mutation updatePlan($id: ID!, $input: UpdatePlanInput!) {
        updatePlan(id: $id, input: $input) {
          id
        }
      }
    `,
    variables: {
      id: payload.id,
      input: payload.input
    }
  });

/**
 * Sync session types (categories) for a plan
 * @param  {string} planId
 * @param  {Array<number>} sessionTypeIds
 * @return {Promise}
 */
const syncSessionTypes = (planId, sessionTypeIds) =>
  client.mutate({
    mutation: gql`
      mutation syncPlanSessionTypes($plan_id: ID!, $session_type_ids: [ID!]!) {
        syncPlanSessionTypes(plan_id: $plan_id, session_type_ids: $session_type_ids) {
          id
          name
          session_types {
            id
            name
          }
        }
      }
    `,
    variables: {
      plan_id: planId,
      session_type_ids: sessionTypeIds
    }
  });

/**
 * Remove the image of a plan
 * @param  {string|number} id
 * @return {Promise}
 */
const removeImage = (id) =>
  client.mutate({
    mutation: gql`
      mutation RemovePlanImage($id: ID!) {
        removePlanImage(id: $id) {
          id
          image_url
        }
      }
    `,
    variables: { id }
  });

/**
 * Upload or replace the cover image of a plan
 * @param  {string|number} id
 * @param  {File} image
 * @return {Promise}
 */
const uploadImage = (id, image) =>
  client.mutate({
    mutation: gql`
      mutation UploadPlanImage($id: ID!, $image: Upload!) {
        uploadPlanImage(id: $id, image: $image) {
          id
          image_url
        }
      }
    `,
    variables: { id, image }
  });

/**
 * Request plan acquisition (MVP: notifies admin until payment gateway is built)
 * @param  {string|number} planId
 * @return {Promise}
 */
const requestAcquisition = (planId) =>
  client.mutate({
    mutation: gql`
      mutation RequestPlanAcquisition($plan_id: ID!) {
        requestPlanAcquisition(plan_id: $plan_id)
      }
    `,
    variables: { plan_id: planId }
  });

export default {
  clientAllowedSessionTypes,
  create,
  find,
  list,
  listAll,
  remove,
  removeImage,
  requestAcquisition,
  restore,
  syncSessionTypes,
  update,
  uploadImage
};
